
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/ui/logo";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { devLog, devError } from "@/utils/devLog";
import { 
  savePendingEnrollment, 
  getEnrollmentFromUrlParams, 
  getPendingEnrollment
} from "@/utils/pendingEnrollment";
import { useEnrollmentRedirect } from "@/hooks/auth/useEnrollmentRedirect";

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  
  const from = location.state?.from as string | undefined;
  
  // Get enrollment data from various sources
  const pendingEnrollment = getPendingEnrollment();
  const { handleEnrollAfterAuth } = useEnrollmentRedirect(
    pendingEnrollment?.courseId,
    pendingEnrollment?.price,
    pendingEnrollment?.courseName,
    pendingEnrollment?.username,
    pendingEnrollment?.courseSlug
  );

  useEffect(() => {
    devLog("[Signup] Component mounted", {
      path: location.pathname,
      from,
      search: location.search,
      pendingEnrollment: !!pendingEnrollment,
      timestamp: new Date().toISOString()
    });
    
    // Check URL parameters for enrollment data and store in sessionStorage
    const enrollmentData = getEnrollmentFromUrlParams(location.search);
    if (enrollmentData?.courseId) {
      savePendingEnrollment({
        ...enrollmentData,
        from: from || location.pathname
      });
      devLog("[Signup] Saved enrollment data from URL parameters", enrollmentData);
    }
    
    return () => {
      devLog("[Signup] Component unmounted", {
        timestamp: new Date().toISOString()
      });
    };
  }, [location, from, pendingEnrollment]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formError) setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("[Signup] Form submitted", { 
      email: formData.email,
      nameProvided: !!formData.name,
      passwordLength: formData.password.length,
      hasPendingEnrollment: !!pendingEnrollment,
      from,
      timestamp: new Date().toISOString()
    });
    
    setFormError(null);
    setIsLoading(true);
    
    try {
      if (formData.password.length < 8) {
        throw new Error("Password must be at least 8 characters long");
      }
      
      console.log("[Signup] Calling supabase.auth.signUp", { 
        email: formData.email,
        timestamp: new Date().toISOString(),
        pendingEnrollment: !!pendingEnrollment
      });
      
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name,
            full_name: formData.name,
          },
        },
      });
      
      if (error) {
        console.error("[Signup] Auth error:", { 
          message: error.message, 
          code: error.code, 
          timestamp: new Date().toISOString() 
        });
        
        // Check for the specific "user already registered" error
        if (error.message.includes("User already registered") || error.status === 422) {
          // Provide a more user-friendly message and suggest login
          setFormError("An account with this email already exists. Please try logging in instead.");
          toast.error("Account already exists. Please log in.");
          setIsLoading(false);
          return;
        }
        
        throw error;
      }
      
      console.log("[Signup] Signup result:", { 
        hasSession: !!data.session,
        userId: data.user?.id,
        timestamp: new Date().toISOString()
      });
      
      if (data.session) {
        console.log("[Signup] Session created, checking for pending enrollments");
        queryClient.clear();
        toast.success("Account created successfully");
        
        // If we have a pending enrollment, redirect directly to dashboard
        // The usePendingEnrollmentRedirect hook will handle processing it
        if (pendingEnrollment) {
          devLog("[Signup] Redirecting to dashboard to process pending enrollment");
          navigate('/dashboard', { replace: true });
          return;
        } else {
          // No enrollment to process, go to regular role selection
          navigate('/role-selection', { 
            state: { 
              isFreshSignup: true 
            } 
          });
        }
      } else {
        console.log("[Signup] No session yet, email confirmation may be required");
        toast.success("Account created! Please check your email for confirmation");
        navigate("/login", { state: { from } });
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      toast.error(error.message || "Failed to create account. Please try again.");
      setFormError(error.message || "Failed to create account. Please try again.");
    } finally {
      setIsLoading(false);
      console.log("[Signup] Form submission complete", { 
        timestamp: new Date().toISOString() 
      });
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex items-center justify-center py-12 px-4 bg-primary-50">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto mb-2">
              <Logo size="lg" />
            </div>
            <CardTitle className="text-2xl font-heading">Create your account</CardTitle>
            <CardDescription>
              Start building your course empire today
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Your name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Create a secure password"
                  required
                  minLength={8}
                  value={formData.password}
                  onChange={handleChange}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must be at least 8 characters
                </p>
              </div>
              
              {formError && (
                <div className="text-sm text-destructive">{formError}</div>
              )}
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Creating account..." : "Create account"}
              </Button>
            </form>
          </CardContent>
          
          <CardFooter>
            <p className="text-center text-sm text-muted-foreground w-full">
              Already have an account?{" "}
              <Link to={`/login`} state={{ from }} className="text-primary font-medium hover:underline">
                Log in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
};

export default Signup;
